import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import CalendarIndividualDates from '../CalendarIndividualDates';

export default function RentItemModal({ open, setOpen }) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    photos: [],
    title: '',
    brand: '',
    model: '',
    description: '',
    location: '',
    condition: '',
    keywords: ['', '', ''],
    charges: { free: false, daily: '', weekly: '', monthly: '' },
    rules: '',
    maxPeriod: '',
    quantity: 1,
    category: '',
    availability: {
      Monday: ['', ''],
      Tuesday: ['', ''],
      Wednesday: ['', ''],
      Thursday: ['', ''],
      Friday: ['', ''],
      Saturday: ['', ''],
      Sunday: ['', '']
    },
    blockDates: []
  });

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: null });
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      ['title', 'brand', 'model', 'description', 'location', 'condition'].forEach((field) => {
        if (!form[field].trim()) newErrors[field] = 'This field is required.';
      });
    } else if (step === 2) {
      if (!form.keywords.some((kw) => kw.trim())) newErrors.keywords = 'At least one keyword is required.';
      if (!form.charges.free && !form.charges.daily && !form.charges.weekly && !form.charges.monthly)
        newErrors.charges = 'Please enter at least one charge or mark as Free.';
      ['rules', 'maxPeriod', 'quantity', 'category'].forEach((field) => {
        if (!form[field]) newErrors[field] = 'This field is required.';
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const inputClass = (field) =>
    `w-full p-2 border rounded ${errors[field] ? 'border-red-500' : 'border-gray-300'} focus:outline-none`;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-3xl rounded bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogTitle className="text-lg font-semibold mb-4">Create Rent Item</DialogTitle>

            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-l"
            >
              &times;
            </button>

            {/* Step indicator */}
            <div className="mb-4">
              <p className="text-sm text-gray-500">Step {step} of 3</p>
              <div className="h-2 bg-gray-200 rounded-full mt-1">
                <div
                  className="h-2 rounded-full bg-blue-500 transition-all"
                  style={{ width: `${(step / 3) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-4">
                {['title', 'brand', 'model', 'description', 'location', 'condition'].map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-medium capitalize">{field}</label>
                    {field === 'description' ? (
                      <textarea
                        className={inputClass(field)}
                        value={form[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                      />
                    ) : (
                      <input
                        type="text"
                        className={inputClass(field)}
                        value={form[field]}
                        onChange={(e) => handleChange(field, e.target.value)}
                      />
                    )}
                    {errors[field] && <p className="text-red-500 text-sm">{errors[field]}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-4">
                <label className="block text-sm font-medium">Keywords</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <input
                      key={i}
                      type="text"
                      className={inputClass('keywords')}
                      value={form.keywords[i]}
                      onChange={(e) => {
                        const newKeywords = [...form.keywords];
                        newKeywords[i] = e.target.value;
                        handleChange('keywords', newKeywords);
                      }}
                    />
                  ))}
                </div>
                {errors.keywords && <p className="text-red-500 text-sm">{errors.keywords}</p>}

                <label className="block text-sm font-medium mt-4">Charges</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.charges.free}
                      onChange={() => handleChange('charges', { ...form.charges, free: !form.charges.free })}
                    />
                    Free
                  </label>
                  {['daily', 'weekly', 'monthly'].map((period) => (
                    <input
                      key={period}
                      type="number"
                      placeholder={`${period.charAt(0).toUpperCase() + period.slice(1)} ($)`}
                      className={inputClass('charges')}
                      onChange={(e) =>
                        handleChange('charges', { ...form.charges, [period]: e.target.value })
                      }
                    />
                  ))}
                </div>
                {errors.charges && <p className="text-red-500 text-sm">{errors.charges}</p>}

                {['rules', 'maxPeriod', 'quantity', 'category'].map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-medium capitalize">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <input
                      type={field === 'rules' ? 'text' : 'number'}
                      className={inputClass(field)}
                      value={form[field]}
                      onChange={(e) => handleChange(field, e.target.value)}
                    />
                    {errors[field] && <p className="text-red-500 text-sm">{errors[field]}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.keys(form.availability).map((day) => (
                    <div key={day}>
                      <label className="block text-sm font-medium text-gray-700">{day}</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="time"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={form.availability[day][0]}
                          onChange={(e) =>
                            handleChange('availability', {
                              ...form.availability,
                              [day]: [e.target.value, form.availability[day][1]]
                            })
                          }
                        />
                        <input
                          type="time"
                          className="w-full p-2 border border-gray-300 rounded"
                          value={form.availability[day][1]}
                          onChange={(e) =>
                            handleChange('availability', {
                              ...form.availability,
                              [day]: [form.availability[day][0], e.target.value]
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">Block Dates</label>
                  <CalendarIndividualDates
                    selectedDates={form.blockDates}
                    onSelectDates={(dates) => handleChange('blockDates', dates)}
                  />
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="mt-6 flex justify-between">
              {step > 1 && (
                <button
                  onClick={prevStep}
                  className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={nextStep}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Add Item
                </button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
